// ============================================================================
// COMPLIANCE CALENDAR — firm-wide, list + calendar views
// ============================================================================
// What the firm owes in a month, across every compliance module.
//
// NOT a GST screen. It reads prima.tbl_obligation, so TDS, Income Tax and ROC
// appear here the day they generate obligations, with no change to this file
// (ADR-0020 §5). That is why it lives under modules/compliance/.
//
// Follows the mandatory screen blueprint in prima-web/CLAUDE.md:
// .module-master-container with no inline styles, ModuleHeader carrying search /
// view-mode / filter config, a single flex wrapper inside .module-content, and
// the filter panel as a SIBLING of that wrapper rather than nested in it.
//
// Grouping is a FILTER, not a toolbar control — engagement type, manager,
// office, module or client. There is no DEPARTMENT option: PRIMA has no
// department master, and the obligation already carries a nullable
// department_id among the Centrix scoping columns, so it becomes a sixth
// grouping for free when Centrix lands (ADR-0021).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { complianceCalendarService } from 'api/services/complianceCalendarService';
import type {
  ComplianceObligation,
  CalendarGroupBy,
} from 'api/types/complianceCalendar.types';

type ViewMode = 'list' | 'calendar';

interface Props {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  allowedActions?: string[];
}

const GROUP_OPTIONS: { value: string; label: string }[] = [
  { value: 'engagementType', label: 'Engagement type' },
  { value: 'manager',        label: 'Manager' },
  { value: 'office',         label: 'Office' },
  { value: 'module',         label: 'Module' },
  { value: 'client',         label: 'Client' },
];

const MODULE_OPTIONS = [
  { value: '',    label: 'All modules' },
  { value: 'GST', label: 'GST' },
  { value: 'TDS', label: 'TDS' },
  { value: 'IT',  label: 'Income Tax' },
  { value: 'ROC', label: 'ROC' },
];

const FILED_OPTIONS = [
  { value: 'open', label: 'Not yet filed' },
  { value: 'all',  label: 'All' },
];

const monthBounds = (d: Date) => ({
  from: new Date(d.getFullYear(), d.getMonth(), 1),
  to: new Date(d.getFullYear(), d.getMonth() + 1, 0),
});

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const formatDay = (value?: string | null): string => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

const monthLabel = (d: Date) => d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

const groupValueOf = (o: ComplianceObligation, by: CalendarGroupBy): string => {
  switch (by) {
    case 'engagementType': return o.engagementTypeName || 'No engagement type';
    case 'manager':        return o.managerName || 'No manager';
    case 'office':         return o.officeName || 'No office';
    case 'module':         return o.moduleCode || 'No module';
    case 'client':         return o.clientLegalName || 'No client';
    default:               return '—';
  }
};

const ComplianceCalendarScreen: React.FC<Props> = ({ moduleIcon, tabId, moduleName }) => {
  void tabId; void moduleName;

  const [month, setMonth] = useState(() => new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // Applied filters.
  const [groupBy, setGroupBy] = useState<CalendarGroupBy>('engagementType');
  const [moduleCode, setModuleCode] = useState('');
  const [filedFilter, setFiledFilter] = useState('open');

  // Drafts — pushed to the applied values on Apply, per the blueprint.
  const [draftGroupBy, setDraftGroupBy] = useState<string>('engagementType');
  const [draftModule, setDraftModule] = useState('');
  const [draftFiled, setDraftFiled] = useState('open');

  const [rows, setRows] = useState<ComplianceObligation[]>([]);
  const [undated, setUndated] = useState(0);
  const [loading, setLoading] = useState(false);

  // Split into primitives so the load callback does not depend on a new object
  // every render — the earlier version rebuilt its bounds object each time,
  // which re-ran the effect and made the data flash and vanish.
  const from = useMemo(() => iso(monthBounds(month).from), [month]);
  const to = useMemo(() => iso(monthBounds(month).to), [month]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [items, undatedCount] = await Promise.all([
        complianceCalendarService.getCalendar({
          dueFrom: from,
          dueTo: to,
          moduleCode: moduleCode || undefined,
          openOnly: filedFilter === 'open',
        }),
        complianceCalendarService.getUndatedCount(moduleCode || undefined),
      ]);
      setRows(items);
      setUndated(undatedCount);
    } catch (err: any) {
      notificationService.toast.error(
        err?.response?.data?.message ?? 'Could not load the compliance calendar.',
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [from, to, moduleCode, filedFilter]);

  useEffect(() => { void load(); }, [load]);

  // Search is client-side here, unlike the paged master screens. The window is
  // one month of deadlines — already fully loaded, because grouping and
  // sub-totals need the whole set rather than a page of it.
  const visibleRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((o) =>
      [o.clientLegalName, o.formCode, o.moduleCode, o.engagementCode,
       o.managerName, o.officeName, o.engagementTypeName, o.periodLabel]
        .some((f) => (f ?? '').toLowerCase().includes(q)));
  }, [rows, searchQuery]);

  const columns: SyutGridColumn<ComplianceObligation>[] = useMemo(() => [
    {
      id: 'dueDate',
      header: 'Due',
      accessorKey: 'effectiveDueDate',
      size: 120,
      enableSorting: true,
      // Marks an extended date so a moved deadline is never read as the
      // original — the whole reason the extension was recorded.
      cell: ({ row }) => {
        const o = row.original;
        if (!o.extendedDueDate) return formatDay(o.dueDate);
        return (
          <span title={`Extended from ${formatDay(o.dueDate)}`}>
            <strong>{formatDay(o.extendedDueDate)}</strong>
            <span style={{ color: '#f59e0b' }}> ext</span>
          </span>
        );
      },
    },
    { id: 'moduleCode', header: 'Module', accessorKey: 'moduleCode', size: 90, enableSorting: true },
    { id: 'formCode', header: 'Return', accessorKey: 'formCode', size: 110, enableSorting: true },
    {
      id: 'client',
      header: 'Client',
      accessorKey: 'clientLegalName',
      size: 240,
      enableSorting: true,
      cell: ({ row }) => row.original.clientLegalName || '—',
    },
    {
      id: 'groupValue',
      header: GROUP_OPTIONS.find((g) => g.value === groupBy)?.label ?? 'Group',
      // Reflects whichever grouping the filter selected, so the chosen
      // dimension is visible and sortable in the list rather than only in the
      // calendar.
      accessorFn: (row: ComplianceObligation) => groupValueOf(row, groupBy),
      size: 180,
      enableSorting: true,
    },
    { id: 'periodLabel', header: 'Period', accessorKey: 'periodLabel', size: 130, enableSorting: true },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'workflowStateCode',
      size: 150,
      enableSorting: true,
      cell: ({ row }) => (
        <>
          <span className={row.original.isLate ? 'badge badge-inactive' : 'badge badge-active'}>
            {row.original.workflowStateName || row.original.workflowStateCode || '—'}
          </span>
          {row.original.isLate && (
            <span style={{ color: '#dc2626', fontSize: '0.75rem', marginLeft: 6 }}>late</span>
          )}
        </>
      ),
    },
  ], [groupBy]);

  // ---- calendar view: day → obligations ---------------------------------
  const calendar = useMemo(() => {
    const first = monthBounds(month).from;
    const last = monthBounds(month).to;
    const byDay = new Map<number, ComplianceObligation[]>();

    for (const o of visibleRows) {
      const due = o.extendedDueDate ?? o.dueDate;
      if (!due) continue;
      const d = new Date(due);
      if (Number.isNaN(d.getTime())) continue;
      const day = d.getDate();
      if (!byDay.has(day)) byDay.set(day, []);
      byDay.get(day)!.push(o);
    }

    // Monday-first, matching how Indian working weeks are read.
    const leading = (first.getDay() + 6) % 7;
    const cells: ({ day: number; items: ComplianceObligation[] } | null)[] =
      Array.from({ length: leading }, () => null);

    for (let d = 1; d <= last.getDate(); d += 1) {
      cells.push({ day: d, items: byDay.get(d) ?? [] });
    }
    return cells;
  }, [visibleRows, month]);

  const groupSummary = useMemo(() => {
    const map = new Map<string, number>();
    for (const o of visibleRows) {
      const key = groupValueOf(o, groupBy);
      map.set(key, (map.get(key) ?? 0) + 1);
    }
    return [...map.entries()].sort((a, b) => b[1] - a[1]);
  }, [visibleRows, groupBy]);

  const activeFilterCount = useMemo(
    () => (moduleCode ? 1 : 0) + (filedFilter !== 'open' ? 1 : 0),
    [moduleCode, filedFilter],
  );

  const actionButtons: ActionButton[] = useMemo(() => [
    {
      id: 'prev', icon: <IconSystem.chevronLeft size={18} />, label: 'Previous',
      tooltip: 'Previous month', variant: 'icon' as const,
      onClick: () => setMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1)),
    },
    {
      id: 'today', icon: <IconSystem.calendar size={18} />, label: 'This month',
      tooltip: 'This month', variant: 'icon' as const,
      onClick: () => setMonth(new Date()),
    },
    {
      id: 'next', icon: <IconSystem.chevronRight size={18} />, label: 'Next',
      tooltip: 'Next month', variant: 'icon' as const,
      onClick: () => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1)),
    },
    {
      id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh',
      tooltip: 'Refresh', variant: 'icon' as const,
      onClick: () => { void load(); },
    },
  ], [load]);

  const applyFilters = useCallback(() => {
    setGroupBy(draftGroupBy as CalendarGroupBy);
    setModuleCode(draftModule);
    setFiledFilter(draftFiled);
    setFilterOpen(false);
  }, [draftGroupBy, draftModule, draftFiled]);

  const clearFilters = useCallback(() => {
    setDraftGroupBy('engagementType');
    setDraftModule('');
    setDraftFiled('open');
    setGroupBy('engagementType');
    setModuleCode('');
    setFiledFilter('open');
  }, []);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title={`Compliance Calendar — ${monthLabel(month)}`}
        description="Everything the firm owes this month, across every compliance module. Sorted by the date that actually applies, so an extended deadline appears in the month it moved to."
        icon={
          moduleIcon && (IconSystem as any)[moduleIcon]
            ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
            : <IconSystem.calendar size={18} />
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search client, return, manager…',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List view' },
            { id: 'calendar', icon: <IconSystem.calendar size={16} />, label: 'Calendar', tooltip: 'Calendar view' },
          ],
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>

            {/* An obligation with no deadline cannot be placed on a calendar OF
                deadlines. Omitting it silently is how it goes unnoticed until it
                is overdue, so the gap is stated rather than hidden. */}
            {!loading && undated > 0 && (
              <div
                style={{
                  border: '1px solid #f59e0b', background: 'var(--bg-secondary)',
                  borderRadius: 6, padding: '0.5rem 0.75rem', marginBottom: 8,
                  fontSize: '0.8125rem', color: 'var(--text-secondary)', flexShrink: 0,
                }}
              >
                <strong style={{ color: 'var(--text-primary)' }}>
                  {undated} obligation(s) have no due date
                </strong>{' '}
                and do not appear here. Recompute due dates on the affected engagements, or add a
                due-date rule for the return types that have none.
              </div>
            )}

            {viewMode === 'list' && (
              <SyutGrid
                data={visibleRows as any}
                columns={columns as any}
                enableSorting
                enablePagination
                pageSize={50}
                emptyMessage={loading ? 'Loading…' : `Nothing due in ${monthLabel(month)}.`}
              />
            )}

            {viewMode === 'calendar' && (
              <div
                className="syut-grid-component"
                style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
              >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, flexShrink: 0 }}>
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
                    <div
                      key={d}
                      style={{
                        padding: '0.375rem', textAlign: 'center', fontWeight: 600,
                        fontSize: '0.75rem', background: 'var(--grid-header-bg, #dbeafe)',
                        color: 'var(--text-primary)',
                      }}
                    >
                      {d}
                    </div>
                  ))}
                </div>
                <div
                  style={{
                    display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1,
                    flex: 1, overflow: 'auto', alignContent: 'start',
                    background: 'var(--border-color)',
                  }}
                >
                  {calendar.map((cell, idx) => (
                    <div
                      key={idx}
                      style={{
                        minHeight: 96, padding: '0.375rem', background: 'var(--bg-primary)',
                        opacity: cell ? 1 : 0.4,
                      }}
                    >
                      {cell && (
                        <>
                          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>
                            {cell.day}
                          </div>
                          {cell.items.slice(0, 4).map((o) => (
                            <div
                              key={o.id}
                              title={`${o.clientLegalName ?? ''} · ${o.moduleCode} ${o.formCode} · ${o.periodLabel}`}
                              style={{
                                fontSize: '0.7rem', marginBottom: 2, padding: '1px 4px',
                                borderRadius: 3, background: 'var(--bg-secondary)',
                                borderLeft: `3px solid ${o.isLate ? '#dc2626' : '#2563eb'}`,
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              }}
                            >
                              <strong>{o.formCode}</strong> {groupValueOf(o, groupBy)}
                            </div>
                          ))}
                          {cell.items.length > 4 && (
                            <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                              +{cell.items.length - 4} more
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Filter panel — SIBLING of the flex wrapper, conditionally rendered.
            The `.collapsed` class the template once used is not defined in this
            project's CSS, so a className toggle leaves the panel stuck open. */}
        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Group by"
                  options={GROUP_OPTIONS}
                  value={draftGroupBy}
                  onChange={(v) => setDraftGroupBy((v as string) || 'engagementType')}
                  isClearable={false}
                  size="sm"
                />
                <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                  Sets the grouping column in the list and the label on each calendar entry.
                  Department arrives with Centrix.
                </div>
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Module"
                  options={MODULE_OPTIONS}
                  value={draftModule}
                  onChange={(v) => setDraftModule((v as string) || '')}
                  isClearable={false}
                  size="sm"
                />
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Filing status"
                  options={FILED_OPTIONS}
                  value={draftFiled}
                  onChange={(v) => setDraftFiled((v as string) || 'open')}
                  isClearable={false}
                  size="sm"
                />
                <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                  A calendar is for work still to do, so unfiled is the default — but
                  "what did we file last month" is a real question too.
                </div>
              </div>

              {groupSummary.length > 0 && (
                <div className="filter-section">
                  <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>
                    This month, by {GROUP_OPTIONS.find((g) => g.value === groupBy)?.label.toLowerCase()}
                  </label>
                  {groupSummary.map(([key, count]) => (
                    <div
                      key={key}
                      style={{
                        display: 'flex', justifyContent: 'space-between',
                        fontSize: '0.8125rem', padding: '2px 0', color: 'var(--text-secondary)',
                      }}
                    >
                      <span>{key}</span>
                      <strong style={{ color: 'var(--text-primary)' }}>{count}</strong>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplianceCalendarScreen;
